/**
 * This file holds functions that performs actual state modifications
 * Please refer to https://vuex.vuejs.org/guide/mutations.html for more information
 * Also mutations MUST BE SYNCHRONOUS https://vuex.vuejs.org/guide/mutations.html#mutations-must-be-synchronous
 */
import * as types from "./types";

const mutations = {
  [types.SAVE_CROPPER_STATE](
    state,
    { image = false, cropBox = false, componentName, tool = false }
  ) {
    if (image && cropBox) {
      if (!tool) {
        throw new Error("tool is required to store the cropper state");
      }
      if (!state.features[state.activeUGCIndex].cropper[componentName]) {
        state.features[state.activeUGCIndex].cropper[componentName] = {};
      }
      if (!state.features[state.activeUGCIndex].cropper[componentName][tool]) {
        state.features[state.activeUGCIndex].cropper[componentName][tool] = {};
      }
      state.features[state.activeUGCIndex].cropper[componentName][tool] = {
        image,
        cropBox: {
          height: cropBox.height,
          left: cropBox.left,
          top: cropBox.top,
          width: cropBox.width
        }
      };
    } else {
      state.features[state.activeUGCIndex].cropper[componentName] = null;
    }
  },
  [types.SET_UGC_SIZE](state, data) {
    state.features[state.activeUGCIndex].ugc_size = data;
  },
  [types.SET_UGC_IMAGE](state, { imagePath, temp_image_id, imageFormat, ugc_image_path_noresize }) {
    state.features[state.activeUGCIndex].ugc_image = imagePath;
    state.features[state.activeUGCIndex].ugc_image_path_noresize = ugc_image_path_noresize;
    state.features[state.activeUGCIndex].ugc_image_id = temp_image_id;
    state.features[state.activeUGCIndex].ugc_image_format = imageFormat;
  },
  [types.SET_ACTIVE_UGC_COMPONENT](state, componentName) {
    state.features[state.activeUGCIndex].activeUGCComponent = componentName;
  },
  /**
   * This mutations set the activeUGCComponent rely on ugcFlowComponents object
   */
  [types.CHANGE_UGC_ACTIVE_COMPONENT](state, payload) {
    let currentUgcView = this.getters["ugc/stateManager/getCurrentUgcView"];
    if (
      state.features[state.activeUGCIndex].ugcFlowComponents[currentUgcView] == "undefined" ||
      !state.features[state.activeUGCIndex].ugcFlowComponents[currentUgcView]
    ) {
      if (payload) {
        currentUgcView -= 1;
      } else {
        currentUgcView += 1;
      }
    }
    state.features[state.activeUGCIndex].activeUGCComponent = state.features[state.activeUGCIndex].ugcFlowComponents[currentUgcView];
  },
  /**
   * This mutations either increment or decrement the placement view order
   */
  [types.UPDATE_PLACEMENT_VIEW_ORDER](state, advance) {
    if (advance) {
      state.features[state.activeUGCIndex].placementViewOrder = state.features[state.activeUGCIndex].placementViewOrder + 1;
    } else {
      state.features[state.activeUGCIndex].placementViewOrder = state.features[state.activeUGCIndex].placementViewOrder - 1;
    }
  },
  [types.RESET_PLACEMENT_VIEW_ORDER](state) {
    state.features[state.activeUGCIndex].placementViewOrder = 0;
  },
  /**
   * This mutations set the placement view order index.
   */
  [types.SET_PLACEMENT_VIEW_ORDER](state, payload) {
    state.features[state.activeUGCIndex].placementViewOrder = payload;
  },
  /**
   * This mutations set the ugc flow views.
   * i.e ugc-design-view, ugc-design-view, ugc-almost-done
   */
  [types.SET_UGC_FLOW_VIEWS](state, payload) {
    state.features[state.activeUGCIndex].ugcFlowViews = payload;
  },
  /**
   * This mutations set the ugc flow components.
   * i.e vamp, quarters, quarters
   */
  [types.SET_UGC_FLOW_COMPONENTS](state, payload) {
    state.features[state.activeUGCIndex].ugcFlowComponents = payload;
  },
  /**
   * This mutations save the state manager by component.
   */
  [types.SAVE_STATE_MANAGERS](state, payload) {
    // TODO check for active ugc component here in imageLibrary flow is it correct
    const activeComponent = this.getters["ugc/tempData/getActiveUGCComponent"];
    const currentState = state.features[state.activeUGCIndex].stateManagers[activeComponent] || {};
    currentState.currentStep = payload.currentStep;
    currentState.isPopArtOrTileApplied = payload.isPopArtOrTileApplied;
    currentState.steps = payload.steps;
    currentState.isLoading = payload.isLoading;
    state.features[state.activeUGCIndex].stateManagers[activeComponent] = currentState;
  },
  // TODO remove toolkit options?
  [types.DELETE_TEMP_COMPONENT_SWATCH](state) {
    const activeUGCIndexFeature = state.features[state.activeUGCIndex];
    const isGCC = this.getters["utils/isGCC"];
    const isDesignViewActive =
    activeUGCIndexFeature.ugcViews.indexOf(activeUGCIndexFeature.activeUGCView) === 0;
    if (
      isDesignViewActive &&
      activeUGCIndexFeature.recipe.components[activeUGCIndexFeature.activeUGCComponent] &&
      activeUGCIndexFeature.recipe.components[activeUGCIndexFeature.activeUGCComponent].ugc &&
      !isGCC
    ) {
      delete activeUGCIndexFeature.recipe.components[activeUGCIndexFeature.activeUGCComponent].ugc;
    }
  },
  /**
   * This function clear all the temp data
   */
  [types.CLEAR](state) {
    state.features[state.activeUGCIndex].cropper = {};
    state.features[state.activeUGCIndex].recipe = {};
    state.features[state.activeUGCIndex].responses = {
      assets3d: {},
      placements: []
    };
    state.features[state.activeUGCIndex].stateManagers = {};
    state.features[state.activeUGCIndex].placementOptions = [];
    state.features[state.activeUGCIndex].cropperOptions = {};
    state.features[state.activeUGCIndex].product_images = [];
    state.features[state.activeUGCIndex].rotated = false;
    state.features[state.activeUGCIndex].placementViewOrder = 0;
    state.features[state.activeUGCIndex].activeUGCView = "ugc-design-view";
    state.features[state.activeUGCIndex].ugc_swatch = {};
    state.features[state.activeUGCIndex].ugc_size = null;
    state.features[state.activeUGCIndex].ugcViews = [];
    state.features[state.activeUGCIndex].background_color = null;
    state.features[state.activeUGCIndex].isEdit = false;
    state.features[state.activeUGCIndex].imageScaleX = null;
    state.features[state.activeUGCIndex].isReplaceTool = false;
    state.features[state.activeUGCIndex].replaceComponentFeature = [];
    state.features[state.activeUGCIndex].applyUGCComponents = [];
    state.features[state.activeUGCIndex].continueAndEditFlow = false;
  },
  [types.ADD_UGC_COMPONENT](state, { componentName, defaultUGCSwatchId, ugc, replaceUgc = false, uploadedAnother = false }) {
    let subgroup = state.features[state.activeUGCIndex].ugcFeature && state.features[state.activeUGCIndex].replaceComponentFeature.indexOf(componentName) < 0 ? state.features[state.activeUGCIndex].ugcFeature : "ugc";
    if (state.features[state.activeUGCIndex].ugcFeature && !uploadedAnother) subgroup = state.features[state.activeUGCIndex].ugcFeature;

    if (!componentName || componentName === "undefined") return;
    const { components = {} } = state.features[state.activeUGCIndex].recipe;
    if (!components[componentName]) components[componentName] = {};
    components[componentName].swatch_id = defaultUGCSwatchId;
    components[componentName].material_type = "canvas";
    components[componentName].group = "ugc";
    components[componentName].subgroup = subgroup;
    if (replaceUgc) {
      components[componentName].ugc = JSON.parse(JSON.stringify(ugc));
    } else if (!components[componentName].ugc) {
      components[componentName].ugc = {
        original_image: ugc && ugc.original_image ? { ...ugc.original_image } : {},
        ugc_swatch: {}
      };
    }

    state.features[state.activeUGCIndex].recipe = {
      ...state.features[state.activeUGCIndex].recipe,
      components
    };
  },
  [types.SET_RECIPE](state, recipe) {
    state.features[state.activeUGCIndex].recipe = JSON.parse(JSON.stringify(recipe));
  },
  [types.SAVE_PRODUCT_IMAGES](state, data) {
    state.features[state.activeUGCIndex].productImages = {
      ...data
    };
  },
  [types.REMOVE_ALMOST_DONE_VIEW](state) {
    const index = state.features[state.activeUGCIndex].ugcViews.indexOf("ugc-almost-done") || state.features[state.activeUGCIndex].ugcViews.indexOf("new-ugc-almost-done");
    if (index !== -1) {
      state.features[state.activeUGCIndex].ugcViews.splice(index, 1);
    }
  },
  [types.SET_BACKGROUND](state, background_color = null) {
    if (!background_color || background_color.toUpperCase() === "#FFFFFF") {
      state.features[state.activeUGCIndex].background_color = null;
    } else {
      state.features[state.activeUGCIndex].background_color = `${background_color}`;
    }
  },
  [types.DISABLE_BG](state, payload = true) {
    state.features[state.activeUGCIndex].disableBg = payload;
  },
  [types.SET_BUCKET_SIZE](state, bucketSize) {
    state.features[state.activeUGCIndex].bucketSize = bucketSize;
  },
  [types.SET_TEMP_BUCKET_SIZE](state, tempBucketSize) {
    state.features[state.activeUGCIndex].tempBucketSize = tempBucketSize;
  },
  [types.SET_RECIPE_FOR_DELETION](state, bucketSize) {
    state.features[state.activeUGCIndex].deletePreviousRecipe = bucketSize;
  },
  [types.SET_STORE_MULTIPLE_UGC_SIZES](state, payload) {
    state.features[state.activeUGCIndex].storeMultipleUGCsizes = payload;
  },
  [types.CLEAR_ORIGINAL_IMAGE](state, componentName) {
    if (!componentName || componentName === "undefined") return;
    const { components = {} } = state.features[state.activeUGCIndex].recipe;
    if (components[componentName].ugc) {
      components[componentName].ugc.original_image = {};
    }
  },
  [types.SET_UPLOAD_ANOTHER](state, value) {
    state.features[state.activeUGCIndex].uploadAnother = value;
  },
  [types.SET_SCALE_X](state, value) {
    state.features[state.activeUGCIndex].imageScaleX = value;
  },
  [types.OVERWRITE_UGC_COMPONENT](state, { component, ugc }) {
    state.features[state.activeUGCIndex].recipe.components[component].ugc = JSON.parse(JSON.stringify(ugc));
  },
  [types.IS_REPLACE_TOOL](state, isReplaceTool) {
    state.features[state.activeUGCIndex].isReplaceTool = isReplaceTool;
  },
  [types.SET_UGC_FEATURE](state, ugcFeature) {
    state.features[state.activeUGCIndex].ugcFeature = ugcFeature;
  },
  [types.UPDATE_COMPONENT](state, {componentName, data}) {
    if(!state.features[state.activeUGCIndex].recipe.components[componentName]) return false;

    state.features[state.activeUGCIndex].recipe.components = {
      ...state.features[state.activeUGCIndex].recipe.components,
      [componentName]: {
        ...data
      }
    };
  },
  /**
   * Set the components replace for a feature
   */
  [types.SET_REPLACE_COMPONENT_FEATURE](state, payload) {
    if ( state.features[state.activeUGCIndex].replaceComponentFeature.indexOf(payload) < 0 )
      state.features[state.activeUGCIndex].replaceComponentFeature.push(payload);
  },
  /**
   * Set the component when apply ugc
   */
  [types.SET_APPLY_UGC_COMPONENTS](state, payload) {
    state.features[state.activeUGCIndex].applyUGCComponents.push(payload);
  },
  /**
   * Remove the component when remove ugc
   */
  [types.REMOVE_APPLY_UGC_COMPONENTS](state, payload) {
    const index = state.features[state.activeUGCIndex].applyUGCComponents.indexOf(payload);
    if (index !== -1) {
      state.features[state.activeUGCIndex].applyUGCComponents.splice(index, 1);
    }
  },
  /**
   * @param {String} featureId The UGC feature to activate either "ugc" or "imageLibrary"
   */
  [types.SET_ACTIVE_UGC_INDEX](state, featureId) {
    const index = state.ugcIndex.indexOf(featureId);
    if (index !== -1) {
      state.activeUGCIndex = index;
    } else {
      state.activeUGCIndex = null;
    }
  },
  [types.SET_SELECT_ANOTHER](state, payload) {
    state.features[state.activeUGCIndex].selectAnother = payload;
  },
  [types.HAS_FINISHED_APPLYING](state, payload) {
    state.features[state.activeUGCIndex].hasFinishedApplying = payload;
  },
  [types.SET_CONTINUE_EDIT_FLOW](state, payload) {
    state.features[state.activeUGCIndex].continueAndEditFlow = payload;
  }
};

export default mutations;
